/**
 * Combobox — Searchable dropdown for selecting one or more items from a list.
 *
 * Combobox Component Guidelines
 * - Use Combobox when the list has more than ~7 items or when users benefit from
 *   typing to filter. For short, fixed lists (3-7 items), prefer a Menu or
 *   RadioGroup instead.
 * - Use `ComboboxTriggerButton` for a styled dropdown trigger with chevron.
 *   `ComboboxTrigger` is the unstyled primitive — only use it when rendering a
 *   fully custom trigger element.
 * - Prefer the simple `<Combobox>` for standard single-select with search. Switch
 *   to the composable API when you need multi-select with chips, custom list
 *   rendering, or non-standard layout (e.g. header/footer content).
 * - Group related items with `ComboboxItemGroup` when the list has natural
 *   categories — it helps users scan long lists faster.
 * - Use `startEnhancer` on `ComboboxItem` for icons or color swatches that help
 *   distinguish items visually (e.g. status icons, team avatars).
 * @see https://base-ui.com/react/components/select Base UI Select documentation
 * @see https://ui.shadcn.com/docs/components/combobox shadcn/ui Combobox documentation
 */
import type { EmptyProps } from "@replo/design-system/components/empty/Empty";
import type { InputProps } from "@replo/design-system/components/input/Input";
import type { ListItemContentProps } from "@replo/design-system/components/list-item/ListItem";
import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";

import * as React from "react";

import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Input } from "@replo/design-system/components/input/Input";
import { ListItem } from "@replo/design-system/components/list-item/ListItem";
import { Separator } from "@replo/design-system/components/separator/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import { CheckIcon, ChevronDown, Search, XIcon } from "lucide-react";

const ComboboxRoot = ComboboxPrimitive.Root;

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

interface ComboboxTriggerProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Trigger.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const ComboboxTriggerInner = <ClassName extends string>(
  {
    className,
    UNSAFE_className,
    children,
    ...props
  }: ComboboxTriggerProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <ComboboxPrimitive.Trigger
      ref={ref}
      data-slot="combobox-trigger"
      className={twMerge(
        "[&_svg:not([class*='size-'])]:size-4",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      {children}
    </ComboboxPrimitive.Trigger>
  );
};

const ComboboxTrigger = React.forwardRef(ComboboxTriggerInner) as <
  ClassName extends string,
>(
  props: ComboboxTriggerProps<ClassName> &
    React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

type ComboboxTriggerButtonProps<ClassName extends string = string> =
  React.PropsWithChildren<
    Omit<ComboboxPrimitive.Trigger.Props, "className" | "children"> & {
      className?: LayoutClassName<ClassName>;
      UNSAFE_className?: string;
      startEnhancer?: React.ReactNode;
      endEnhancer?: React.ReactNode;
      size?: "sm" | "base";
      isPlaceholder?: boolean;
    }
  >;

const ComboboxTriggerButtonInner = <ClassName extends string>(
  {
    className,
    UNSAFE_className,
    children,
    startEnhancer,
    endEnhancer,
    size = "sm",
    isPlaceholder = false,
    ...props
  }: ComboboxTriggerButtonProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <ComboboxPrimitive.Trigger
      ref={ref}
      data-slot="combobox-trigger-button"
      render={
        <Button
          variant="secondary"
          size={size}
          startEnhancer={undefined}
          endEnhancer={
            endEnhancer ?? (
              <ChevronDown
                className={twMerge(
                  "text-muted shrink-0 ml-auto",
                  size === "sm" ? "size-3!" : "size-4!",
                )}
              />
            )
          }
          UNSAFE_className={twMerge(
            "justify-start bg-sheet hover:bg-surface justify-between data-popup-open:bg-surface",
            UNSAFE_className,
          )}
        />
      }
      className={twMerge("[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      <div className="flex items-center gap-1 overflow-hidden text-placeholder">
        {startEnhancer}
        <span
          className={twMerge(
            "truncate",
            isPlaceholder ? "text-placeholder" : "text-default",
          )}
        >
          {children}
        </span>
      </div>
    </ComboboxPrimitive.Trigger>
  );
};

const ComboboxTriggerButton = React.forwardRef(ComboboxTriggerButtonInner) as <
  ClassName extends string,
>(
  props: ComboboxTriggerButtonProps<ClassName> &
    React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

function ComboboxInput({
  disabled = false,
  wrapperRef,
  size,
  ...props
}: Omit<ComboboxPrimitive.Input.Props, "className" | "size"> & InputProps) {
  return (
    <ComboboxPrimitive.Input
      render={<Input disabled={disabled} wrapperRef={wrapperRef} size={size} />}
      {...props}
    />
  );
}

interface ComboboxContentProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Popup.Props, "className">,
    Pick<
      ComboboxPrimitive.Positioner.Props,
      "side" | "align" | "sideOffset" | "alignOffset" | "anchor"
    > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxContent({
  className,
  UNSAFE_className,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxContentProps) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-[150]"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={Boolean(anchor)}
          className={twMerge(
            "bg-white border border-0.5 border-border text-default data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:bg-surface *:data-[slot=input-group]:border-component-border overflow-hidden rounded-lg shadow-xl duration-100 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:shadow-none data-[side=inline-start]:slide-in-from-right-2 data-[side=inline-end]:slide-in-from-left-2 group/combobox-content relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-(--anchor-width) origin-(--transform-origin) data-[chips=true]:min-w-(--anchor-width)",
            className,
            UNSAFE_className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

interface ComboboxListProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.List.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const ComboboxListInner = <ClassName extends string>(
  { className, UNSAFE_className, ...props }: ComboboxListProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return (
    <ComboboxPrimitive.List
      ref={ref}
      data-slot="combobox-list"
      className={twMerge(
        "flex flex-col gap-1 no-scrollbar max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 p-1 data-empty:p-0 overflow-y-auto overscroll-contain",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
};

const ComboboxList = React.forwardRef(ComboboxListInner) as <
  ClassName extends string,
>(
  props: ComboboxListProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

export interface ComboboxItemProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Item.Props, "className" | "children">,
    Omit<ListItemContentProps, "endEnhancer"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxItem<ClassName extends string>({
  className,
  UNSAFE_className,
  startEnhancer,
  size,
  variant,
  selected,
  inset,
  label,
  description,
  ...props
}: ComboboxItemProps<ClassName>) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      render={
        <ListItem
          label={label}
          className={className}
          UNSAFE_className={twMerge(
            "aria-selected:bg-menu-item-selected",
            UNSAFE_className,
          )}
          startEnhancer={startEnhancer}
          size={size}
          variant={variant}
          selected={selected}
          inset={inset}
          description={description}
          endEnhancer={
            <ComboboxPrimitive.ItemIndicator>
              <CheckIcon />
            </ComboboxPrimitive.ItemIndicator>
          }
        />
      }
      {...props}
    />
  );
}

interface ComboboxGroupProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Group.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxGroup<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxGroupProps<ClassName>) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={twMerge(className, UNSAFE_className)}
      {...props}
    />
  );
}

interface ComboboxLabelProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.GroupLabel.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxLabel<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxLabelProps<ClassName>) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={twMerge(
        "text-default header-h6 px-2 py-1",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return (
    <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
  );
}

interface ComboboxEmptyProps<ClassName extends string = string>
  extends Omit<
      ComboboxPrimitive.Empty.Props,
      "className" | "title" | "description"
    >,
    Omit<
      EmptyProps<ClassName>,
      "className" | "UNSAFE_className" | "title" | "description"
    > {
  title?: string;
  description?: string;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxEmpty<ClassName extends string>({
  className,
  UNSAFE_className,
  title = "No results found",
  description = "Try a different search.",
  size = "sm",
  ...props
}: ComboboxEmptyProps<ClassName>) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={twMerge(
        "text-muted hidden w-full justify-center py-6 text-center text-sm group-data-empty/combobox-content:flex",
        className,
        UNSAFE_className,
      )}
      render={<Empty size={size} title={title} description={description} />}
      {...props}
    />
  );
}

function ComboboxSeparator({
  ...props
}: Omit<ComboboxPrimitive.Separator.Props, "className">) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      // NOTE (Ben, 2026-02-12): This is a hack to ensure the separator is the full width of the combobox.
      render={<Separator className="min-w-[calc(100%+1rem)] -ml-1" />}
      {...props}
    />
  );
}

interface ComboboxChipsProps<ClassName extends string = string>
  extends Omit<
    React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
      ComboboxPrimitive.Chips.Props,
    "className"
  > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxChips<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxChipsProps<ClassName>) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={twMerge(
        "border-component-border focus-within:border-ring focus-within:ring-ring/50 flex min-h-8 flex-wrap items-center gap-1 rounded-lg border bg-transparent bg-clip-padding px-2.5 py-1 text-sm transition-colors focus-within:ring-3 has-data-[slot=combobox-chip]:px-1",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface ComboboxChipProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Chip.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  showRemove?: boolean;
}

function ComboboxChip<ClassName extends string>({
  className,
  UNSAFE_className,
  children,
  showRemove = true,
  ...props
}: ComboboxChipProps<ClassName>) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={twMerge(
        "bg-surface text-default flex h-[calc(--spacing(5.25))] w-fit items-center justify-center gap-1 rounded-sm px-1.5 text-xs font-medium whitespace-nowrap has-data-[slot=combobox-chip-remove]:pr-0 has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          className="-ml-1 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
          render={
            <Button variant="tertiary" icon size="sm">
              <XIcon className="pointer-events-none" />
            </Button>
          }
        />
      )}
    </ComboboxPrimitive.Chip>
  );
}

interface ComboboxChipsInputProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Input.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxChipsInput<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxChipsInputProps<ClassName>) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={twMerge(
        "min-w-16 flex-1 outline-none",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

interface ComboboxHeaderProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxHeader<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxHeaderProps<ClassName>) {
  return (
    <div
      data-slot="combobox-header"
      className={twMerge(
        "flex flex-col gap-2 p-3 rounded-t-lg border-border border-b-0.5",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}
interface ComboboxTitleProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxTitle<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxTitleProps<ClassName>) {
  return (
    <div
      data-slot="combobox-title"
      className={twMerge("text-default header-h6", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface ComboboxFooterProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxFooter<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxFooterProps<ClassName>) {
  return (
    <div
      data-slot="combobox-footer"
      className={twMerge(
        "p-3 rounded-b-lg border-border border-t-0.5",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

export type ComboboxItemWithValue = Omit<ComboboxItemProps, "value"> & {
  value: string;
};

export interface ComboboxItemGroup {
  value: string;
  label: React.ReactNode;
  items: ComboboxItemWithValue[];
}

function isGroupedItems(
  items: Readonly<ComboboxItemWithValue[]> | Readonly<ComboboxItemGroup[]>,
): items is Readonly<ComboboxItemGroup[]> {
  return items.length > 0 && items[0] != null && "items" in items[0];
}

interface ComboboxProps<ClassName extends string = string>
  extends Omit<
    ComboboxPrimitive.Root.Props<ComboboxItemWithValue>,
    "children" | "items"
  > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  title?: string;
  footer?: React.ReactNode;
  trigger: React.ReactElement;
  placeholder?: string;
  empty?: React.ReactNode;
  triggerAsChild?: boolean;
  items: Readonly<ComboboxItemWithValue[]> | Readonly<ComboboxItemGroup[]>;
}

function Combobox<ClassName extends string = string>({
  className,
  UNSAFE_className,
  title,
  footer,
  trigger,
  items,
  placeholder,
  empty,
  triggerAsChild = false,
  ...props
}: ComboboxProps<ClassName>) {
  return (
    <ComboboxRoot
      items={items}
      itemToStringValue={(item: ComboboxItemWithValue) => String(item.label)}
      {...props}
    >
      {triggerAsChild ? (
        <ComboboxTrigger render={trigger} />
      ) : (
        <ComboboxTrigger>{trigger}</ComboboxTrigger>
      )}

      <ComboboxContent UNSAFE_className={twMerge(className, UNSAFE_className)}>
        <ComboboxHeader>
          {title && <ComboboxTitle>{title}</ComboboxTitle>}

          <ComboboxInput startEnhancer={<Search />} placeholder={placeholder} />
        </ComboboxHeader>

        <ComboboxEmpty>{empty}</ComboboxEmpty>

        <ComboboxList>
          {isGroupedItems(items)
            ? (group: ComboboxItemGroup, index: number) => (
                <React.Fragment key={group.value}>
                  {index > 0 && <ComboboxSeparator />}
                  <ComboboxGroup items={group.items}>
                    <ComboboxLabel>{group.label}</ComboboxLabel>
                    <ComboboxCollection>
                      {(item: ComboboxItemWithValue) => (
                        <ComboboxItem key={item.value} {...item} value={item} />
                      )}
                    </ComboboxCollection>
                  </ComboboxGroup>
                </React.Fragment>
              )
            : (item: ComboboxItemWithValue) => (
                <ComboboxItem key={item.value} {...item} value={item} />
              )}
        </ComboboxList>

        {footer && <ComboboxFooter>{footer}</ComboboxFooter>}
      </ComboboxContent>
    </ComboboxRoot>
  );
}

export {
  Combobox,
  ComboboxRoot,
  ComboboxInput,
  ComboboxContent,
  ComboboxHeader,
  ComboboxTitle,
  ComboboxFooter,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxTriggerButton,
  ComboboxValue,
  useComboboxAnchor,
};
